import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { MainList } from "./MainList";
import { SubList } from "./SubList";
import { MainContent } from "./MainContent";
import { useProtocolData } from "../../hooks/protocoldata/useProtocolData";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { useSnackbar } from "notistack";

export const AddProtocolDialog = ({ open, onClose, onAdd }) => {
  const { token } = useAuthContext();

  const [selectedMainId, setSelectedMainId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const { areas, conditions } = useProtocolData(token);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const mainItems = areas.map((area, index) => ({
    id: index,
    name: area.area,
  }));

  const subItems =
    selectedMainId !== null
      ? conditions[mainItems[selectedMainId]?.name] || []
      : [];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { name: "" },
  });

  useEffect(() => {
    setValue("name", "");
  }, [open, setValue]);

  useEffect(() => {
    if (open) {
      setSelectedMainId(null); // Clear the MainList selection
      setSelectedSubId(null); // Clear the SubList selection
      setSelectedExercises([]); // Clear selected exercises
    }
  }, [open]);

  const onSubmit = async (data) => {
    let conditionID;
    const areaName = mainItems[selectedMainId]?.name;
    const aconditionName = subItems[selectedSubId];

    if (!areaName) {
      enqueueSnackbar("Select an Area!", { variant: "error" });
      return;
    }

    if (!aconditionName) {
      enqueueSnackbar("Select an Condition!", { variant: "error" });
      return;
    }
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/condition/getid`,
      {
        area: areaName,
        acondition: aconditionName,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    conditionID = response.data;

    if (selectedExercises.length === 0) {
      enqueueSnackbar("Select at least one exercise!", { variant: "error" });
      return;
    }
    const newProtocolData = {
      protocolname: data.name,
      conditions: `,${conditionID}`,
      exercises: selectedExercises,
    };
    onAdd(newProtocolData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>Add Protocol</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ mt: 3, mb: 4 }}
          >
            <Box flexGrow={1} sx={{ mr: 6 }}>
              <FormControl fullWidth>
                <TextField
                  label="Protocol Name"
                  placeholder="Enter Protocol Name"
                  {...register("name", {
                    required: "Protocol Name is required",
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </FormControl>
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                height: "64px",
                mt: "45px",
                px: 8,
                py: 2,
                textAlign: "center",
              }}
            >
              Add
            </Button>
          </Box>
        </form>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <MainList
            mainItems={mainItems}
            selectedMainId={selectedMainId}
            onMainSelect={(mainId) => {
              setSelectedMainId(mainId); // Set the selected main item
              setSelectedSubId(null); // Clear the sublist selection
            }}
          />
          <SubList
            subItems={subItems.map((name, index) => ({
              id: index,
              name,
            }))}
            selectedSubId={selectedSubId}
            onSubSelect={setSelectedSubId}
          />
          <MainContent
            selectedMainId={mainItems[selectedMainId]?.name}
            selectedSubId={subItems[selectedSubId]}
            onSelectedExercisesChange={(selected) =>
              setSelectedExercises(selected)
            }
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
